import React, { useEffect, useState } from "react";
import "../styles/pagos.css";
import { anularPago } from "../../../../services/pagosService";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";

const currency = new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" });

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const buildPrintHtml = (data, mode) => {
  const isMatricial = mode === "matricial76mm";
  const is60mm = mode === "60mm";
  const isThermal = isMatricial || is60mm;
  const size = mode === "a4" ? "A4" : is60mm ? "60mm auto" : isMatricial ? "76mm auto" : "80mm auto";
  const width = mode === "a4" ? "210mm" : is60mm ? "56mm" : isMatricial ? "66mm" : "80mm";
  const padding = mode === "a4" ? "12mm" : is60mm ? "0mm 0mm 0mm 2mm" : isMatricial ? "0mm 0mm 0mm 2mm" : "6mm";
  const cardBorder = mode === "a4" ? "1px solid #000000" : "none";
  const cardRadius = mode === "a4" ? "12px" : "0";
  const fontSize = isThermal ? "9pt" : "12px";
  const lineStyle = isThermal ? "border-top: 1px solid #000000" : "border-top: 1px dashed rgba(15, 23, 42, 0.35)";
  const reciboNo = escapeHtml(data?.reciboNo ?? "");
  const fecha = escapeHtml(formatDate(data?.fecha));
  const negocio = escapeHtml(data?.negocio || "CENTRO DE FORMACION");
  const direccion = escapeHtml(data?.direccion || "-");
  const ruc = escapeHtml(data?.ruc || "-");
  const cliente = escapeHtml(data?.cliente || "-");
  const rucCi = escapeHtml(data?.rucCi || "-");
  /* const dirCliente = escapeHtml(data?.dirCliente || "-"); */
  const matriculaLabel = data?.codigo_matricula
    ? data.codigo_matricula
    : data?.codigoMatricula
      ? data.codigoMatricula
      : data?.matriculaId
        ? `#${data.matriculaId}`
        : "-";
  const matriculaId = escapeHtml(matriculaLabel);
  const curso = escapeHtml(data?.curso || "-");
  const metodoPago = escapeHtml(data?.metodoPago || "-");
  const concepto = escapeHtml(data?.concepto || "Pago de curso");
  const numeroFactura = data?.contifico_numero_factura || null;
  const totalNumber = Number(data?.totalCurso ?? 0);
  const descuentoPromocionMonto = Number(data?.descuentoPromocionMonto ?? 0);
  const descuentoIndividualMonto = Number(data?.descuentoIndividualMonto ?? 0);
  const descuentoTotalNumber = Math.max(descuentoPromocionMonto + descuentoIndividualMonto, 0);
  const subtotalNumber = Number.isFinite(Number(data?.subtotal))
    ? Number(data?.subtotal)
    : totalNumber > 0
      ? totalNumber / 1.15
      : 0;
/*   const precioNumber = Number.isFinite(Number(data?.precioLista)) && Number(data?.precioLista) > 0
    ? Number(data?.precioLista)
    : Math.max(subtotalNumber + descuentoTotalNumber, 0); */
  const ivaNumber = Number.isFinite(Number(data?.impuestos))
    ? Number(data?.impuestos)
    : Math.max(totalNumber - subtotalNumber, 0);
  const montoNumber = Number(data?.monto ?? 0);
  const recargoMontoNumber = Number(data?.recargo_monto ?? 0);
  const saldoMatriculaActual = Number(data?.saldoPendiente ?? 0);
  const afectaMatricula = data?.afectaMatricula ?? true;
  const tipoPago = data?.tipo || (afectaMatricula ? 'matricula' : 'cargos');
  const esConstancia = tipoPago === 'constancia';
  const showPricing = tipoPago !== 'cargos' && !esConstancia;
  const cargosAdicionales = Array.isArray(data?.cargosAdicionales) ? data.cargosAdicionales : [];
  const cargosMontoTotal = cargosAdicionales.reduce((sum, c) => sum + Number(c.monto || 0), 0);
  // saldoCargosRestantes: cargos que siguen pendientes después de este pago (viene del backend)
  const saldoCargosRestantes = Number(data?.saldoCargosRestantes ?? 0);
  // PAGO REALIZADO:
  // - afectaMatricula: pago.monto = solo matrícula, cargos se suman aparte
  // - !afectaMatricula: pago.monto ya ES el total cobrado (incluye los cargos)
  // En ambos casos, recargo_monto siempre se suma (es dinero adicional cobrado)
  const totalCobradoNumber = afectaMatricula
    ? montoNumber + recargoMontoNumber + cargosMontoTotal
    : montoNumber + recargoMontoNumber;
  // SALDO ACTUAL: matrícula restante + cargos aún pendientes
  const saldoActualNumber = saldoMatriculaActual + saldoCargosRestantes;
  // SALDO ANTERIOR: identidad contable — siempre igual a PAGO REALIZADO + SALDO ACTUAL
  const saldoAnteriorNumber = Math.max(totalCobradoNumber + saldoActualNumber, 0);
  const formatMoney = (value) => escapeHtml(currency.format(roundMoney(value || 0)));
  const monto = formatMoney(totalCobradoNumber);
/*   const precio = formatMoney(precioNumber); */
  const subtotal15 = formatMoney(subtotalNumber);
  const descuento = formatMoney(descuentoTotalNumber);
  const iva = formatMoney(ivaNumber);
  const total = formatMoney(totalNumber);
  const saldoAnterior = formatMoney(saldoAnteriorNumber);
  const saldoActual = formatMoney(saldoActualNumber);
  const cajero = escapeHtml(data?.cajero || "-");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Comprobante ${reciboNo}</title>
    <style>
      @page { size: ${size}; margin: 0; }
      * { color: #000000 !important; }
      body {
        margin: ${isThermal ? "0 auto" : "0"};
        padding: ${padding};
        width: ${width};
        box-sizing: border-box;
        font-family: "Times New Roman", serif;
        font-size: ${fontSize};
        line-height: ${isThermal ? "1.3" : "1.5"};
        color: #000000;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .card {
        border: ${cardBorder};
        border-radius: ${cardRadius};
        padding: ${isThermal ? "4px" : "16px"};
      }
      .header { text-align: center; }
      .title { font-weight: 700; font-size: ${isThermal ? "9pt" : "14px"}; letter-spacing: 0.05em; }
      .header-sub { font-size: ${isThermal ? "9pt" : "inherit"}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .line { ${lineStyle}; margin: ${isThermal ? "3px 0" : "12px 0"}; }
      .row { display: flex; gap: 4px; justify-content: space-between; }
      .row.headline { font-weight: 700; letter-spacing: 0.1em; }
      .label { color: #000000; min-width: ${isThermal ? "70px" : "90px"}; font-weight: ${isThermal ? "700" : "400"}; flex-shrink: 0; font-size: ${isThermal ? "9pt" : "inherit"}; }
      .value { text-align: right; flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-size: ${isThermal ? "9pt" : "inherit"}; }
      .section { display: flex; flex-direction: column; gap: ${isThermal ? "1px" : "4px"}; }
      .detail { font-weight: 700; font-size: ${isThermal ? "9pt" : "inherit"}; }
      .total { display: flex; justify-content: space-between; font-size: ${isThermal ? "9pt" : "inherit"}; }
      .total.strong { font-weight: 700; }
      .footer { margin-top: ${isThermal ? "4px" : "12px"}; display: flex; flex-direction: column; gap: ${isThermal ? "2px" : "6px"}; font-size: ${isThermal ? "9pt" : "inherit"}; }
      .muted { text-align: center; }
      .anulado-watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-35deg); font-size: ${isThermal ? "36pt" : "72pt"}; font-weight: 900; color: rgba(220,38,38,0.18); letter-spacing: 0.1em; pointer-events: none; z-index: 999; white-space: nowrap; }
      .anulado-banner { background: #fee2e2; border: 1px solid #fca5a5; border-radius: 4px; padding: ${isThermal ? "2px 4px" : "6px 10px"}; text-align: center; font-weight: 700; color: #991b1b; font-size: ${isThermal ? "9pt" : "13px"}; margin-bottom: ${isThermal ? "3px" : "8px"}; }
    </style>
  </head>
  <body>
    ${data?.anulado ? `<div class="anulado-watermark">ANULADO</div>` : ''}
    <div class="card">
      ${data?.anulado ? `<div class="anulado-banner">⚠ PAGO ANULADO${data.deletedAt ? ' — ' + new Date(data.deletedAt).toLocaleString() : ''}</div>` : ''}
      <div class="header">
        <div class="title">${negocio}</div>
        <div class="header-sub">${direccion}</div>
        <div class="header-sub">R.U.C.: ${ruc}</div>
      </div>
      <div class="line"></div>
      <div class="row headline">
        <span>${esConstancia ? "CONSTANCIA DE MATRÍCULA" : "RECIBO No."}</span>
        <span class="value">${reciboNo}</span>
      </div>
      ${esConstancia ? '<div style="text-align:center; font-size:10px; color:#6b7280;">Cubierto por pago empresarial</div>' : ''}
      <div class="section" style="margin-top: ${isThermal ? "2px" : "8px"};">
        <div class="row"><span class="label">Fecha:</span><span class="value">${fecha}</span></div>
        <div class="row"><span class="label">Cliente:</span><span class="value">${cliente}</span></div>
        <div class="row"><span class="label">RUC / CI:</span><span class="value">${rucCi}</span></div>
        <div class="row"><span class="label">Codigo matricula:</span><span class="value">${matriculaId}</span></div>
        <div class="row"><span class="label">Curso:</span><span class="value">${curso}</span></div>
        <div class="row"><span class="label">Metodo:</span><span class="value">${metodoPago}</span></div>
      </div>
      <div class="line"></div>
      <div class="section">
        <div class="label">Detalle</div>
        <div class="detail">${concepto}</div>
        ${cargosAdicionales.length > 0 ? `
        <div style="margin-top:4px; display:flex; flex-direction:column; gap:2px;">
          ${data.afectaMatricula ? `<div class="total"><span>Saldo de matricula</span><span class="value">${formatMoney(montoNumber)}</span></div>` : ""}
          ${cargosAdicionales.map((c) => `<div class="total"><span>${escapeHtml(c.concepto_nombre)}</span><span class="value">${formatMoney(Number(c.monto))}</span></div>`).join("")}
        </div>` : ""}
      </div>
      ${showPricing ? `
      <div class="line"></div>
      <div class="section">
        <div class="total"><span>SUBTOTAL 15%</span><span class="value">${subtotal15}</span></div>
        <div class="total"><span>DESCUENTO</span><span class="value">${descuento}</span></div>
        <div class="total"><span>IVA 15%</span><span class="value">${iva}</span></div>
        <div class="total strong"><span>TOTAL CURSO</span><span class="value">${total}</span></div>
      </div>` : ''}
      ${recargoMontoNumber > 0 ? `
      <div class="line"></div>
      <div class="section">
        <div class="total"><span>Pago base</span><span class="value">${formatMoney(montoNumber)}</span></div>
        <div class="total"><span>Recargo tarjeta</span><span class="value">${formatMoney(recargoMontoNumber)}</span></div>
      </div>` : ''}
      <div class="line"></div>
      <div class="section">
        <div class="total"><span>SALDO ANTERIOR</span><span class="value">${saldoAnterior}</span></div>
        <div class="total strong"><span>PAGO REALIZADO</span><span class="value">${monto}</span></div>
        <div class="total"><span>SALDO ACTUAL</span><span class="value">${saldoActual}</span></div>
      </div>
      ${numeroFactura ? `
      <div class="line"></div>
      <div class="section" style="font-size:${isThermal ? '8pt' : '10px'}; color:#6b7280;">
        <div>Factura electr&oacute;nica: <strong>${escapeHtml(numeroFactura)}</strong></div>
      </div>` : ''}
      <div class="footer">
        <div>Cajero: ${cajero}</div>
        <div class="muted">Gracias por su pago</div>
      </div>
    </div>
  </body>
</html>`;
};

export const buildCompanyPrintHtml = (data, mode) => {
  const isMatricial = mode === "matricial76mm";
  const is60mm = mode === "60mm";
  const isThermal = isMatricial || is60mm;
  const size = mode === "a4" ? "A4" : is60mm ? "60mm auto" : isMatricial ? "76mm auto" : "80mm auto";
  const width = mode === "a4" ? "210mm" : is60mm ? "56mm" : isMatricial ? "66mm" : "80mm";
  const padding = mode === "a4" ? "12mm" : is60mm ? "0mm 0mm 0mm 2mm" : isMatricial ? "0mm 0mm 0mm 2mm" : "6mm";
  const cardBorder = mode === "a4" ? "1px solid #000000" : "none";
  const cardRadius = mode === "a4" ? "12px" : "0";
  const fontSize = isThermal ? "9pt" : "12px";
  const lineStyle = isThermal ? "border-top: 1px solid #000000" : "border-top: 1px dashed rgba(15,23,42,.35)";
  const fmt = (v) => escapeHtml(currency.format(roundMoney(v || 0)));
  const pagos = Array.isArray(data?.pagos) ? data.pagos : [];
  const filas = pagos.map((p) => `
    <tr>
      <td>${escapeHtml(p.estudiante)}</td>
      <td>${escapeHtml(p.codigoMatricula || `#${p.pagoId}`)}</td>
      <td style="text-align:right">${fmt(p.monto)}</td>
    </tr>`).join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Comprobante Empresarial ${escapeHtml(data?.reciboNo || "")}</title>
    <style>
      @page { size: ${size}; margin: 0; }
      * { color: #000000 !important; }
      body { margin:${isThermal ? "0 auto" : "0"}; padding:${padding}; width:${width}; box-sizing:border-box; font-family:"Times New Roman",serif; font-size:${fontSize}; line-height:${isThermal ? "1.3" : "1.5"}; color:#000000; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      .card { border:${cardBorder}; border-radius:${cardRadius}; padding:${isThermal ? "4px" : "16px"}; }
      .header { text-align:center; }
      .title { font-weight:700; font-size:${isThermal ? "9pt" : "14px"}; letter-spacing:.05em; }
      .line { ${lineStyle}; margin:${isThermal ? "3px 0" : "10px 0"}; }
      .row { display:flex; gap:4px; justify-content:space-between; }
      .label { color:#000000; min-width:${isThermal ? "70px" : "90px"}; font-weight:${isThermal ? "700" : "400"}; flex-shrink:0; font-size:${isThermal ? "9pt" : "inherit"}; }
      .value { text-align:right; flex:1; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; font-size:${isThermal ? "9pt" : "inherit"}; }
      .section { display:flex; flex-direction:column; gap:${isThermal ? "1px" : "4px"}; }
      table { width:100%; border-collapse:collapse; font-size:${isThermal ? "9pt" : "11px"}; margin-top:6px; }
      th { text-align:left; border-bottom:1px solid #000000; padding:2px 0; font-weight:700; }
      th:last-child, td:last-child { text-align:right; }
      td { padding:2px 0; }
      .total-row { font-weight:700; border-top:1px solid #000000; }
      .footer { margin-top:${isThermal ? "4px" : "12px"}; display:flex; flex-direction:column; gap:${isThermal ? "2px" : "6px"}; }
      .muted { text-align:center; }
    </style>
  </head>
  <body>
    <div class="card" style="${data?.anulado ? 'border-color:#fca5a5;' : ''}">
      <div class="header">
        <div class="title">${escapeHtml(data?.negocio || "CENTRO DE FORMACION")}</div>
        <div>${escapeHtml(data?.direccion || "")}</div>
        <div>R.U.C.: ${escapeHtml(data?.ruc || "-")}</div>
      </div>
      <div class="line"></div>
      <div class="row" style="font-weight:700; letter-spacing:.15em;">
        <span>COMPROBANTE EMPRESARIAL</span>
        <span class="value">${escapeHtml(data?.reciboNo || "")}</span>
      </div>
      <div class="section" style="margin-top:8px;">
        <div class="row"><span class="label">Fecha:</span><span class="value">${escapeHtml(formatDate(data?.fecha))}</span></div>
        <div class="row"><span class="label">Empresa:</span><span class="value">${escapeHtml(data?.empresa || "-")}</span></div>
        <div class="row"><span class="label">RUC empresa:</span><span class="value">${escapeHtml(data?.empresaRuc || "-")}</span></div>
        <div class="row"><span class="label">Método:</span><span class="value">${escapeHtml(data?.metodoPago || "-")}</span></div>
        <div class="row"><span class="label">Referencia:</span><span class="value">${escapeHtml(data?.referencia || "-")}</span></div>
      </div>
      <div class="line"></div>
      <div style="font-weight:600; margin-bottom:4px;">Matrículas cubiertas</div>
      <table>
        <thead><tr><th>Estudiante</th><th>Matrícula</th><th>Monto</th></tr></thead>
        <tbody>${filas}</tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="2">TOTAL PAGADO</td>
            <td>${fmt(data?.montoTotal)}</td>
          </tr>
          ${data?.saldoFavor > 0 ? `<tr><td colspan="2">Saldo a favor</td><td>${fmt(data?.saldoFavor)}</td></tr>` : ""}
        </tfoot>
      </table>
      <div class="footer">
        <div>Cajero: ${escapeHtml(data?.cajero || "-")}</div>
        ${data?.observaciones ? `<div>${escapeHtml(data.observaciones)}</div>` : ""}
        <div class="muted">Documento interno — no válido como factura fiscal</div>
      </div>
    </div>
  </body>
</html>`;
};

export const printHtml = (html) => {
  if (typeof window === "undefined") return;
  const existing = document.getElementById("comprobante-print-frame");
  if (existing?.parentNode) existing.parentNode.removeChild(existing);

  const iframe = document.createElement("iframe");
  iframe.id = "comprobante-print-frame";
  // Fuera de pantalla pero con ancho real para que flexbox y layouts se calculen correctamente
  iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:400px;height:600px;border:0;visibility:hidden;";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }

  doc.open();
  doc.write(html);
  doc.close();

  const printWin = iframe.contentWindow;
  if (!printWin) return;

  printWin.onafterprint = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  setTimeout(() => {
    try {
      // Para papel térmico/matricial el driver puede imponer una altura fija (ej. 297mm).
      // Medimos el contenido real y sobreescribimos @page con la altura exacta.
      const thermalMatch = html.match(/size:\s*(\d+)mm\s+auto/);
      if (thermalMatch) {
        const widthMm = thermalMatch[1];
        const heightPx = doc.body.scrollHeight;
        const heightMm = Math.ceil(heightPx / 96 * 25.4) + 4; // +4mm margen al corte
        const s = doc.createElement("style");
        s.textContent = `@page { size: ${widthMm}mm ${heightMm}mm !important; margin: 0; }`;
        doc.head.appendChild(s);
      }
    } catch { /* silencioso */ }
    printWin.focus();
    printWin.print();
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 2000);
  }, 300);
};

export default function PagoDetailPanel({
  pago,
  onClose,
  comprobante,
  comprobanteLoading,
  onFetchComprobante,
  onAnularPago,
}) {
  const { hasPermission } = usePermissions();
  const canAnular = hasPermission("pagos.anular");
  const [printMode, setPrintMode] = useState("matricial76mm");
  const [anularConfirm, setAnularConfirm] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const reciboNo =
    comprobante && String(comprobante.pagoId ?? comprobante.reciboNo) === String(pago?.id)
      ? comprobante.reciboNo
      : null;

  useEffect(() => {
    if (!pago?.id || !onFetchComprobante) return;
    if (reciboNo != null) return;
    onFetchComprobante(pago.id);
  }, [pago?.id, onFetchComprobante, reciboNo]);

  useEffect(() => {
    setAnularConfirm(false);
    setAnulando(false);
  }, [pago?.id]);

  const handleAnular = async () => {
    if (!pago?.id) return;
    setAnulando(true);
    try {
      await anularPago(pago.id);
      showToast({ type: "success", message: "Pago anulado correctamente." });
      setAnularConfirm(false);
      onAnularPago?.();
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "Error al anular el pago." });
    } finally {
      setAnulando(false);
    }
  };

  const handlePrint = async () => {
    if (!pago?.id) return;
    let data = comprobante;
    const matchesPago = data && String(data.pagoId ?? data.reciboNo) === String(pago.id);
    if ((!data || !matchesPago) && onFetchComprobante) {
      data = await onFetchComprobante(pago.id);
    }
    if (!data) return;
    const html = buildPrintHtml({ ...data, anulado: pago.anulado, deletedAt: pago.deletedAt }, printMode);
    printHtml(html);
  };


  if (!pago) {
    return (
      <div className="management-card pago-detail empty h-100">
        <p className="text-muted mb-1">Selecciona un pago para ver el detalle.</p>
        <small className="text-muted">Tip: filtra por estudiante, codigo o referencia.</small>
      </div>
    );
  }

  return (
    <div className="management-card pago-detail h-100 d-flex flex-column">
      {pago.anulado && (
        <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ borderRadius: 6 }}>
          <i className="bi bi-slash-circle-fill" />
          <div>
            <strong>Pago anulado</strong>
            {pago.deletedAt && (
              <div className="small">{new Date(pago.deletedAt).toLocaleString()}</div>
            )}
          </div>
        </div>
      )}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="management-eyebrow mb-1">Detalle de pago</p>
          <h6 className="mb-0">
            Pago #{reciboNo ?? (comprobanteLoading ? "..." : "—")}
          </h6>
          <small className="text-muted">Registrado: {pago.fechaPago ? new Date(pago.fechaPago).toLocaleString() : "-"}</small>
        </div>
        <button type="button" className="btn btn-link text-decoration-none text-muted p-0" onClick={onClose}>
          Cerrar
        </button>
      </div>

      {pago.contificoNumeroFactura && (
        <div className="mb-3 d-flex align-items-center gap-2">
          <span className="badge bg-success">
            <i className="bi bi-receipt me-1" />FAC
          </span>
          <small className="text-success fw-semibold">{pago.contificoNumeroFactura}</small>
        </div>
      )}

      <div className="detail-grid mb-3">
        <div>
          <p className="text-muted mb-1">Codigo matricula</p>
          <h6 className="mb-0">{pago.codigoMatricula || (pago.matriculaId ? `#${pago.matriculaId}` : "N/D")}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Estudiante</p>
          <h6 className="mb-0">{pago.estudiante || "No disponible"}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Monto</p>
          <h6 className="mb-0">{currency.format(pago.monto || 0)}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Metodo</p>
          <h6 className="mb-0 text-capitalize">{pago.metodoPago}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Concepto</p>
          <h6 className="mb-0 text-capitalize">
            {pago.afectaMatricula
              ? "Matrícula"
              : pago.concepto && pago.concepto.toLowerCase() !== "cargos adicionales"
                ? pago.concepto
                : "Cargos"}
          </h6>
        </div>
        <div>
          <p className="text-muted mb-1">{pago.afectaMatricula ? "Estado pago de matricula" : "Estado cargo"}</p>
          <h6 className="mb-0 text-capitalize">{pago.afectaMatricula ? (pago.estadoPago || "Sin estado") : (pago.cargoEstado || "pagado")}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Estado matricula</p>
          <h6 className="mb-0 text-capitalize">{pago.estadoMatricula || "No disponible"}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Referencia</p>
          <h6 className="mb-0">{pago.referencia || "No registrada"}</h6>
        </div>
      </div>

      <div className="comprobante-actions">
        <div>
          <p className="text-muted mb-1">Comprobante</p>
          <small className="text-muted">Selecciona el formato y presiona imprimir.</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm"
            value={printMode}
            onChange={(e) => setPrintMode(e.target.value)}
          >
            <option value="60mm">Termica 60mm</option>
            <option value="matricial76mm">Matricial 76mm</option>
            <option value="a4">A4</option>
          </select>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center"
            onClick={handlePrint}
            disabled={comprobanteLoading}
            title="Imprimir"
          >
            <i className="bi bi-printer" />
          </button>
        </div>
      </div>
      {(printMode === "matricial76mm" || printMode === "60mm") && (
        <small className="text-muted d-block mt-1">
          <i className="bi bi-info-circle me-1" />
          En el diálogo de impresión: selecciona tu impresora matricial, desactiva encabezados/pies de página y elige el tamaño de papel correcto ({printMode === "matricial76mm" ? "76 mm" : "60 mm"}).
        </small>
      )}

      {comprobanteLoading && (
        <div className="text-muted small d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-arrow-repeat spin" />
          <span>Cargando comprobante...</span>
        </div>
      )}

      {canAnular && !pago.anulado && (
        <div className="mt-auto pt-3 border-top">
          {!anularConfirm ? (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm w-100"
              onClick={() => setAnularConfirm(true)}
            >
              <i className="bi bi-trash me-2" />
              Anular pago
            </button>
          ) : (
            <div className="d-flex flex-column gap-2">
              <small className="text-danger fw-semibold">
                ¿Confirmas la anulación? El saldo de la matrícula se revertirá.
              </small>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger btn-sm flex-fill"
                  onClick={handleAnular}
                  disabled={anulando}
                >
                  {anulando
                    ? <><i className="bi bi-arrow-repeat spin me-1" />Anulando...</>
                    : "Confirmar"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm flex-fill"
                  onClick={() => setAnularConfirm(false)}
                  disabled={anulando}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
